import { Home, FileText, List, Plus, NotepadText, ChevronDown } from "lucide-react";
import { useLocation, Link } from "react-router-dom";
import { useMemo } from "react";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarSeparator,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getProfilePictureInstant } from "@/utils/profilePicture";

interface User {
  name?: string;
  email: string;
  id?: string;
}

interface AppSidebarProps {
  user: User | null;
  onLogout: () => void;
}

export const navigationGroups = [
  {
    title: "Home",
    url: "/",
    icon: Home,
  },
  {
    title: "KPI Questions",
    icon: FileText,
    items: [
      {
        title: "Create Question List",
        url: "/create-kpi-question-list",
      },
      {
        title: "View All Question Lists",
        url: "/view-all-kpi-question-list",
      },
    ],
  },
  {
    title: "KPI Forms",
    icon: List,
    items: [
      {
        title: "Create Form",
        url: "/create-kpi-form",
      },
      {
        title: "View All Forms",
        url: "/view-all-kpi-form",
      },
      {
        title: "Archived Forms",
        url: "/view-all-kpi-form-archived",
      },
    ],
  },
  {
    title: "My KPI",
    icon: NotepadText,
    items: [
      {
        title: "My KPI",
        url: "/kpi-form-user",
      },
      {
        title: "All User KPI Forms",
        url: "/view-all-user-kpi-form",
      },
    ],
  },
];

export function AppSidebar({ user, onLogout }: AppSidebarProps) {
  const location = useLocation();

  const avatarUrl = useMemo(() => {
    if (!user) return null;
    const response = getProfilePictureInstant(user.id);
    if (response) {
      return response;
    }
    return null;
  }, [user?.id]);

  const userInitials = useMemo(() => {
    if (!user?.name) return "U";
    return user.name
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase())
      .join("")
      .substring(0, 2);
  }, [user?.name]);

  return (
    <Sidebar collapsible="icon" variant="inset">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link to="/">
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                  <NotepadText className="size-4" />
                </div>
                <div className="flex flex-col gap-0.5 leading-none">
                  <span className="font-semibold">Duo KPI</span>
                  {/* <span className="text-xs">Manage your KPI questions</span> */}
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarSeparator />

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {navigationGroups.map((item) => {
                // Simple link without sub-items
                if (item.url) {
                  return (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        asChild
                        isActive={location.pathname === item.url}
                        tooltip={item.title}
                      >
                        <Link to={item.url}>
                          <item.icon />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                }

                // Collapsible menu with sub-items
                return (
                  <Collapsible
                    key={item.title}
                    defaultOpen
                    className="group/collapsible"
                  >
                    <SidebarMenuItem>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton tooltip={item.title}>
                          <item.icon />
                          <span>{item.title}</span>
                          <ChevronDown className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-180" />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          {item.items?.map((subItem) => (
                            <SidebarMenuSubItem key={subItem.title}>
                              <SidebarMenuSubButton
                                asChild
                                isActive={location.pathname === subItem.url}
                              >
                                <Link to={subItem.url}>
                                  <span>{subItem.title}</span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          ))}
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <div className="flex flex-col gap-2 p-2">
              {user && (
                <div className="flex items-center gap-2 group-data-[collapsible=icon]:justify-center">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={avatarUrl || undefined} alt={user.name} />
                    <AvatarFallback>{userInitials}</AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col gap-1 group-data-[collapsible=icon]:hidden">
                    <div className="text-sm font-medium truncate">
                      {user.name || "User"}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {user.email}
                    </div>
                  </div>
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={onLogout}
                className="w-full group-data-[collapsible=icon]:aspect-square group-data-[collapsible=icon]:p-0"
              >
                <span className="group-data-[collapsible=icon]:hidden">
                  Sign Out
                </span>
                <span className="hidden group-data-[collapsible=icon]:inline">
                  ↗
                </span>
              </Button>
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
